import type { GameInstance, InputState } from '@arcraz/arcadia-types';

/**
 * Browser game loop. Fixed-timestep accumulator at 60Hz; render decoupled
 * via {@link requestAnimationFrame}.
 *
 * Mirrors the headless loop in `@arcraz/arcadia-core` so a replay produced
 * here can be re-simulated in Node bit-for-bit. Both paths call
 * `instance.input(state)` then `instance.update(TICK_SECONDS)` per tick;
 * the only thing this file adds is rAF pacing and `instance.render(ctx)`.
 *
 * **Determinism in the browser:**
 * - The accumulator clamps elapsed time to 250ms so a backgrounded tab
 *   doesn't try to catch up by running 600 ticks at once on resume.
 * - Cartridge code never sees real elapsed time — only a fixed `dt`.
 * - The cabinet records replay frames at the input layer (which lives
 *   above this file in `input.ts`) — this loop just runs them.
 */

/** Tick duration in seconds. Always 1/60, exact. Mirror of headless loop's. */
export const TICK_SECONDS = 1 / 60;
/** Tick duration in milliseconds. */
export const TICK_MS = 1000 / 60;
/**
 * Hard cap on accumulated time per `frame()` call. Prevents catch-up
 * spirals after a tab regains focus.
 */
const MAX_ACCUMULATED_MS = 250;

/**
 * Source of input snapshots. Implementations live in `input.ts` (real
 * keyboard/touch/gamepad) and tests (mock recorder/replayer).
 */
export interface InputSource {
  /** Returns the input state to feed `instance.input` for the next tick. */
  snapshot(): InputState;
}

/** Public handle returned by {@link startLoop}. Lets callers pause/stop the loop. */
export interface LoopHandle {
  /** Pause the loop — does not call `instance.pause()`; caller's responsibility. */
  pause(): void;
  /** Resume from pause. */
  resume(): void;
  /** Stop entirely. After this, the handle is dead. Calls `instance.teardown()`. */
  stop(): void;
  /** Whether the loop is currently running. */
  isRunning(): boolean;
}

/**
 * Optional dependency injection for testing. Production callers should
 * leave these undefined — they default to `performance.now` and
 * `requestAnimationFrame`/`cancelAnimationFrame`.
 */
export interface LoopDeps {
  now?: () => number;
  requestFrame?: (cb: FrameRequestCallback) => number;
  cancelFrame?: (handle: number) => void;
}

/**
 * Drive a {@link GameInstance} on a 60Hz fixed-timestep accumulator.
 *
 * @param instance The cartridge's running session.
 * @param ctx 2D context to pass to `instance.render`.
 * @param input Source of per-tick input snapshots.
 * @param deps Optional injection points for testing.
 * @returns Handle for pause/stop control.
 */
export function startLoop(
  instance: GameInstance,
  ctx: CanvasRenderingContext2D,
  input: InputSource,
  deps: LoopDeps = {}
): LoopHandle {
  const now = deps.now ?? (() => performance.now());
  const requestFrame = deps.requestFrame ?? requestAnimationFrame.bind(globalThis);
  const cancelFrame = deps.cancelFrame ?? cancelAnimationFrame.bind(globalThis);

  let accumulator = 0;
  let lastTime = now();
  let running = true;
  let paused = false;
  let rafHandle: number | null = null;

  const frame = (): void => {
    if (!running) {
      return;
    }
    const current = now();

    if (paused) {
      // While paused, freeze accumulator so we don't accrue catch-up time.
      lastTime = current;
      rafHandle = requestFrame(frame);
      return;
    }

    const elapsed = Math.min(current - lastTime, MAX_ACCUMULATED_MS);
    lastTime = current;
    accumulator += elapsed;

    while (accumulator >= TICK_MS) {
      instance.input(input.snapshot());
      instance.update(TICK_SECONDS);
      accumulator -= TICK_MS;
    }

    instance.render(ctx);
    rafHandle = requestFrame(frame);
  };

  rafHandle = requestFrame(frame);

  return {
    pause(): void {
      paused = true;
    },
    resume(): void {
      paused = false;
      lastTime = now();
    },
    stop(): void {
      if (!running) {
        return;
      }
      running = false;
      if (rafHandle !== null) {
        cancelFrame(rafHandle);
        rafHandle = null;
      }
      instance.teardown();
    },
    isRunning(): boolean {
      return running;
    },
  };
}
